import { useState, useEffect, useCallback, useRef, useMemo, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { 
  Flame, 
  Droplets, 
  Dumbbell, 
  Camera,
  TrendingUp,
  Trophy,
  ChevronRight,
  Zap,
  Apple,
  Target,
  Sparkles,
  Heart,
  ShoppingCart,
  Clock,
  Search,
  Loader2,
  ListPlus,
  Calendar,
  CalendarPlus,
  Pin,
  Bell,
  Award,
  X,
  MapPin,
  Edit,
  Trash2,
  AlertCircle,
  Stethoscope,
  Share2
} from 'lucide-react';

// Critical components - loaded immediately
import StreakFlame from '@/components/StreakFlame';
import MicroGoals from '@/components/MicroGoals';
import NotificationsBell from '@/components/NotificationsBell';
import { celebrateAchievement, celebrateBig } from '@/utils/celebrations';

// Lazy-loaded components - loaded on demand for better initial performance
const QuickActionsSheet = lazy(() => import('@/components/QuickActionsSheet'));
const WeatherWidget = lazy(() => import('@/components/WeatherWidget'));
const MiniWeightChart = lazy(() => import('@/components/MiniWeightChart'));
const RecipeCarousel = lazy(() => import('@/components/RecipeCarousel'));
const NutritionChatbot = lazy(() => import('@/components/NutritionChatbot'));
const RecipeGenerator = lazy(() => import('@/components/RecipeGenerator'));

// Lightweight loading fallback
const LazyFallback = () => (
  <div className="animate-pulse bg-muted/30 rounded-lg h-24 w-full" />
);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [stats, setStats] = useState(null);
  const [dailySummary, setDailySummary] = useState(null);
  const [challenges, setChallenges] = useState(null);
  const [motivation, setMotivation] = useState(null);
  const [dailyRecipes, setDailyRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date().toISOString().split('T')[0]);
  
  // User stats & badges
  const [userStats, setUserStats] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  
  // Bariatric daily reminder
  const [showBariatricReminder, setShowBariatricReminder] = useState(false);
  const [bariatricReminderData, setBariatricReminderData] = useState(null);
  
  // Appointments
  const [appointments, setAppointments] = useState([]);
  const [todayAppointments, setTodayAppointments] = useState([]);
  const [showAppointmentDialog, setShowAppointmentDialog] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [newAppointment, setNewAppointment] = useState({
    title: '',
    type: 'medical',
    date: new Date().toISOString().split('T')[0],
    time: '09:00',
    location: '',
    notes: '',
    pinned: false
  });
  
  // Smart recommendation popup
  const [showRecommendation, setShowRecommendation] = useState(false);
  const [recommendation, setRecommendation] = useState(null);
  const lastRecommendationTime = useRef(Date.now());
  const RECOMMENDATION_INTERVAL = 45 * 60 * 1000; // 45 minutes
  
  // AI Recipe Search state
  const [recipeSearchQuery, setRecipeSearchQuery] = useState('');
  const [searchedRecipe, setSearchedRecipe] = useState(null);
  const [loadingSearch, setLoadingSearch] = useState(false);
  
  // Recipe Modal state
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [showRecipeModal, setShowRecipeModal] = useState(false);
  
  // Share achievement state
  const [showShareAchievementDialog, setShowShareAchievementDialog] = useState(false);
  const [achievementToShare, setAchievementToShare] = useState(null);
  const [userGroups, setUserGroups] = useState([]);
  const [selectedShareTarget, setSelectedShareTarget] = useState('public');
  
  // Weather & City
  const [userCity, setUserCity] = useState(() => localStorage.getItem('user_city') || '');
  
  // Fetch city from backend on mount
  useEffect(() => {
    const fetchUserCity = async () => {
      try {
        const res = await axios.get(`${API}/user/city`, { withCredentials: true });
        if (res.data.city) {
          setUserCity(res.data.city);
          localStorage.setItem('user_city', res.data.city);
        }
      } catch (error) {
        // Use localStorage fallback
      }
    };
    fetchUserCity();
  }, []);
  
  // Handle city change
  const handleCityChange = async (city) => {
    setUserCity(city);
    localStorage.setItem('user_city', city);
    try {
      await axios.post(`${API}/user/city`, { city }, { withCredentials: true });
    } catch (error) {
      console.error('Error saving city:', error);
    }
  };
  
  // Weight history for mini chart
  const [weightHistory, setWeightHistory] = useState([]);

  // Check for midnight reset
  useEffect(() => {
    const checkDateChange = () => {
      const today = new Date().toISOString().split('T')[0];
      if (today !== currentDate) {
        console.log('New day detected, refreshing data...');
        setCurrentDate(today);
        setDailySummary(null);
        fetchDashboardData();
      }
    };

    const interval = setInterval(checkDateChange, 60000);
    
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkDateChange();
        checkRecommendationTimer();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [currentDate]);

  // Smart recommendation timer
  const checkRecommendationTimer = useCallback(async () => {
    const now = Date.now();
    if (now - lastRecommendationTime.current >= RECOMMENDATION_INTERVAL) {
      lastRecommendationTime.current = now;
      await fetchSmartRecommendation();
    }
  }, []);

  useEffect(() => {
    const interval = setInterval(checkRecommendationTimer, 60000); // Check every minute
    return () => clearInterval(interval);
  }, [checkRecommendationTimer]);

  useEffect(() => {
    fetchDashboardData();
    fetchUserStats();
    fetchAppointments();
    checkBariatricReminder();
    fetchUserGroups();
    
    // Show recommendation after 45 minutes of first load
    const initialTimeout = setTimeout(() => {
      fetchSmartRecommendation();
    }, RECOMMENDATION_INTERVAL);
    
    return () => clearTimeout(initialTimeout);
  }, []);

  // Check for bariatric daily reminder
  const checkBariatricReminder = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/bariatric/daily-reminder`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      
      if (response.data.show_reminder && !sessionStorage.getItem('bariatricReminderShown')) {
        setBariatricReminderData(response.data);
        setShowBariatricReminder(true);
      }
    } catch (error) {
      // User might not have bariatric profile - that's OK
    }
  };

  const dismissBariatricReminder = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/bariatric/dismiss-reminder`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      sessionStorage.setItem('bariatricReminderShown', 'true');
      setShowBariatricReminder(false);
    } catch (error) {
      setShowBariatricReminder(false);
    }
  };

  // Show today's appointments alert on load
  useEffect(() => {
    if (todayAppointments.length > 0 && !sessionStorage.getItem('todayAppointmentsShown')) {
      sessionStorage.setItem('todayAppointmentsShown', 'true');
      toast.info(`📅 Vous avez ${todayAppointments.length} rendez-vous aujourd'hui !`, {
        duration: 5000,
        action: {
          label: 'Voir',
          onClick: () => document.getElementById('appointments-section')?.scrollIntoView({ behavior: 'smooth' })
        }
      });
    }
  }, [todayAppointments]);

  const fetchDashboardData = async () => {
    const today = new Date().toISOString().split('T')[0];
    
    try {
      const [statsRes, summaryRes, challengesRes, motivationRes, recipesRes, weightRes] = await Promise.allSettled([
        axios.get(`${API}/progress/stats`, { withCredentials: true }),
        axios.get(`${API}/food/daily-summary?date=${today}`, { withCredentials: true }),
        axios.get(`${API}/challenges`, { withCredentials: true }),
        axios.get(`${API}/motivation`, { withCredentials: true }),
        axios.get(`${API}/recipes/daily`, { withCredentials: true }),
        axios.get(`${API}/progress/weight-history?days=7`, { withCredentials: true }),
      ]);
      
      if (statsRes.status === 'fulfilled') setStats(statsRes.value.data);
      if (summaryRes.status === 'fulfilled') {
        const summaryData = summaryRes.value.data;
        if (summaryData.date === today) {
          setDailySummary(summaryData);
        } else {
          setDailySummary({
            date: today,
            consumed: { calories: 0, protein: 0, carbs: 0, fat: 0 },
            targets: summaryData.targets || { calories: 2000, protein: 100, carbs: 250, fat: 65 },
            remaining: summaryData.targets || { calories: 2000, protein: 100, carbs: 250, fat: 65 },
            meals_logged: 0
          });
        }
      }
      if (challengesRes.status === 'fulfilled') setChallenges(challengesRes.value.data);
      if (motivationRes.status === 'fulfilled') setMotivation(motivationRes.value.data);
      if (recipesRes.status === 'fulfilled') setDailyRecipes(recipesRes.value.data?.recipes || []);
      if (weightRes.status === 'fulfilled') setWeightHistory(weightRes.value.data || []);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserStats = async () => {
    try {
      const [statsRes, profileRes] = await Promise.allSettled([
        axios.get(`${API}/user/stats`, { withCredentials: true }),
        axios.get(`${API}/profile`, { withCredentials: true })
      ]);
      
      if (statsRes.status === 'fulfilled') setUserStats(statsRes.value.data);
      if (profileRes.status === 'fulfilled') setUserProfile(profileRes.value.data);
    } catch (error) {
      console.error('Error fetching user stats:', error);
    }
  };

  const fetchAppointments = async () => {
    try {
      const [allRes, todayRes] = await Promise.allSettled([
        axios.get(`${API}/appointments`, { withCredentials: true }),
        axios.get(`${API}/appointments/today`, { withCredentials: true }),
      ]);
      
      if (allRes.status === 'fulfilled') setAppointments(allRes.value.data || []);
      if (todayRes.status === 'fulfilled') setTodayAppointments(todayRes.value.data || []);
    } catch (error) {
      console.error('Error fetching appointments:', error);
    }
  };

  const fetchSmartRecommendation = async () => {
    try {
      const response = await axios.get(`${API}/recommendations/smart`, { withCredentials: true });
      setRecommendation(response.data);
      setShowRecommendation(true);
    } catch (error) {
      console.error('Error fetching recommendation:', error);
    }
  };

  const fetchUserGroups = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/social/user-groups`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setUserGroups(response.data?.groups || []);
    } catch (error) {
      console.error('Error loading user groups:', error);
    }
  };

  const shareAchievement = async (type, data, targetWall = 'public') => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/social/share-achievement`, {
        type,
        data,
        target_wall: targetWall
      }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      const wallName = targetWall === 'public' ? 'le mur public' : 'votre groupe';
      toast.success(`Partagé sur ${wallName} ! +15 points 🎉`);
      setShowShareAchievementDialog(false);
    } catch (error) {
      toast.error('Erreur lors du partage');
    }
  };

  const addRecipeToFavorites = async (recipe) => {
    try {
      await axios.post(`${API}/recipes/favorites`, { recipe }, { withCredentials: true });
      toast.success('Recette ajoutée aux favoris !');
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('Recette déjà dans les favoris');
      } else {
        console.error('Error adding to favorites:', error);
        toast.error('Erreur lors de l\'ajout');
      }
    }
  };

  const addIngredientsToShoppingList = async (ingredients) => {
    if (!ingredients || ingredients.length === 0) {
      toast.error('Aucun ingrédient à ajouter');
      return;
    }
    try {
      const items = ingredients.map(i => ({ 
        item: i.item || i.name || String(i), 
        quantity: i.quantity || '' 
      })).filter(i => i.item && i.item.trim());
      
      if (items.length === 0) {
        toast.error('Aucun ingrédient valide');
        return;
      }
      
      const response = await axios.post(`${API}/shopping-list/bulk`, { items }, { withCredentials: true });
      toast.success(`${response.data.added_count || items.length} ingrédients ajoutés !`);
    } catch (error) {
      console.error('Error adding ingredients:', error);
      toast.error('Erreur lors de l\'ajout');
    }
  };

  const getNutriScoreColor = (score) => {
    const colors = {
      'A': 'bg-green-500',
      'B': 'bg-lime-500',
      'C': 'bg-yellow-500',
      'D': 'bg-orange-500',
      'E': 'bg-red-500',
    };
    return colors[score] || 'bg-gray-400';
  };

  const searchRecipeByAI = async () => {
    if (!recipeSearchQuery.trim()) {
      toast.error('Veuillez entrer une description de recette');
      return;
    }
    
    setLoadingSearch(true);
    setSearchedRecipe(null);
    
    try {
      const response = await axios.post(`${API}/recipes/search`, { 
        query: recipeSearchQuery.trim()
      }, { withCredentials: true });
      
      if (response.data.recipe) {
        setSearchedRecipe(response.data.recipe);
        toast.success('Recette trouvée !');
      } else {
        toast.error('Aucune recette générée');
      }
    } catch (error) {
      console.error('Error searching recipe:', error);
      toast.error('Erreur lors de la recherche');
    } finally {
      setLoadingSearch(false);
    }
  };

  const handleSaveAppointment = async () => {
    if (!newAppointment.title || !newAppointment.date || !newAppointment.time) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    try {
      if (editingAppointment) {
        await axios.put(`${API}/appointments/${editingAppointment.appointment_id}`, newAppointment, { withCredentials: true });
        toast.success('Rendez-vous modifié');
      } else {
        await axios.post(`${API}/appointments`, newAppointment, { withCredentials: true });
        toast.success('Rendez-vous créé');
      }
      
      setShowAppointmentDialog(false);
      setEditingAppointment(null);
      setNewAppointment({
        title: '',
        type: 'medical',
        date: new Date().toISOString().split('T')[0],
        time: '09:00',
        location: '',
        notes: '',
        pinned: false
      });
      fetchAppointments();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDeleteAppointment = async (appointmentId) => {
    try {
      await axios.delete(`${API}/appointments/${appointmentId}`, { withCredentials: true });
      toast.success('Rendez-vous supprimé');
      fetchAppointments();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const togglePinAppointment = async (apt) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/appointments/${apt.appointment_id}`, 
        { pinned: !apt.pinned }, 
        { 
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true 
        }
      );
      fetchAppointments();
      toast.success(apt.pinned ? 'Désépinglé' : 'Épinglé ✓');
    } catch (error) {
      console.error('Pin error:', error);
      toast.error('Erreur lors de l\'épinglage');
    }
  };

  const appointmentTypeLabels = {
    medical: { label: 'Médical', icon: '🏥', color: 'bg-red-100 text-red-700' },
    sport: { label: 'Sport', icon: '🏃', color: 'bg-green-100 text-green-700' },
    wellness: { label: 'Bien-être', icon: '🧘', color: 'bg-purple-100 text-purple-700' },
    nutrition: { label: 'Nutrition', icon: '🥗', color: 'bg-orange-100 text-orange-700' }
  };

  // Memoized calculations for better performance - MUST be before any conditional returns
  const calorieProgress = useMemo(() => {
    return dailySummary 
      ? Math.min((dailySummary.consumed.calories / dailySummary.targets.calories) * 100, 100)
      : 0;
  }, [dailySummary]);

  // Memoized sorted appointments
  const sortedAppointments = useMemo(() => {
    return [...appointments].sort((a, b) => {
      if (a.pinned && !b.pinned) return -1;
      if (!a.pinned && b.pinned) return 1;
      const today = new Date().toISOString().split('T')[0];
      if (a.date === today && b.date !== today) return -1;
      if (a.date !== today && b.date === today) return 1;
      return new Date(a.date) - new Date(b.date);
    });
  }, [appointments]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="w-16 h-16 rounded-full gradient-primary" />
          <p className="text-muted-foreground">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-safe" data-testid="dashboard-page">
      {/* Header - Sans marge supérieure */}
      <header className="sticky top-0 z-10 px-4 pt-2 pb-3 bg-background/80 backdrop-blur-lg border-b border-border safe-area-top">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-xl font-bold">
              {t('hello')}, {user?.name?.split(' ')[0]} 👋
            </h1>
            <p className="text-sm text-muted-foreground">
              {new Date().toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { weekday: 'long', day: 'numeric', month: 'long' })}
            </p>
          </div>
          <div className="flex items-center gap-1">
            <NotificationsBell language={language} />
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate('/profile')}
              className="relative"
            >
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center overflow-hidden">
                {(userStats?.picture || user?.picture) ? (
                  <img src={userStats?.picture || user?.picture} alt="Profile" className="w-full h-full rounded-full object-cover" />
                ) : (
                  <span className="text-white font-bold">{user?.name?.[0]}</span>
                )}
              </div>
            </Button>
          </div>
        </div>
      </header>

      <main className="p-4 space-y-4 max-w-lg mx-auto">
        {/* Micro Goals Widget */}
        <Card className="bg-gradient-to-br from-muted/30 to-muted/10">
          <CardHeader className="pb-2 pt-3 px-4">
            <CardTitle className="text-sm flex items-center gap-2">
              <Target className="w-4 h-4 text-primary" />
              {language === 'fr' ? 'Objectifs du jour' : 'Today\'s Goals'}
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-3">
            <MicroGoals
              steps={dailySummary?.steps || 0}
              stepGoal={10000}
              water={dailySummary?.water_glasses || 0}
              waterGoal={8}
              meals={dailySummary?.meals_logged || 0}
              mealGoal={3}
              workout={dailySummary?.workout_done || false}
              weighed={dailySummary?.weighed || false}
              language={language}
            />
          </CardContent>
        </Card>

        {/* Days Counter with Streak Flame & Mini Weight Chart */}
        {userStats && (
          <div className="grid grid-cols-2 gap-2">
            <Card className="bg-gradient-to-br from-primary/10 to-primary/5">
              <CardContent className="p-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Série' : 'Streak'}</p>
                  <StreakFlame streak={userStats.streak} size="md" />
                </div>
                <p className="text-xs text-muted-foreground">{language === 'fr' ? 'jours de suite' : 'consecutive days'}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-secondary/10 to-secondary/5">
              <CardContent className="p-3">
                <Suspense fallback={<LazyFallback />}>
                  <MiniWeightChart data={weightHistory} height={50} language={language} />
                </Suspense>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Weather Widget */}
        <Suspense fallback={<LazyFallback />}>
          <WeatherWidget 
            city={userCity} 
            onCityChange={handleCityChange}
            language={language}
          />
        </Suspense>

        {/* Recent Badges Display - Show last 3 earned badges */}
        {userStats?.recent_badges && userStats.recent_badges.length > 0 && (
          <div className="flex gap-2 overflow-x-auto pb-2 hide-scrollbar">
            {userStats.recent_badges.slice(0, 3).map((badge) => (
              <div 
                key={badge.id}
                className="flex-shrink-0 px-3 py-2 rounded-full bg-gradient-to-r from-primary/20 to-secondary/20 border border-primary/30 flex items-center gap-2 cursor-pointer hover:bg-primary/30 transition-colors"
                title={badge.description}
                onClick={() => {
                  setAchievementToShare({ type: 'badge', data: badge });
                  setShowShareAchievementDialog(true);
                  celebrateAchievement('badge');
                }}
              >
                <span className="text-lg">{badge.icon}</span>
                <span className="text-xs font-medium">{badge.name}</span>
                <Share2 className="w-3 h-3 text-muted-foreground" />
              </div>
            ))}
          </div>
        )}

        {/* Today's Appointments Alert */}
        {todayAppointments.length > 0 && (
          <Card className="border-primary/50 bg-primary/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                  <Bell className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-sm">RDV aujourd'hui</p>
                  {todayAppointments.slice(0, 2).map((apt, i) => (
                    <p key={i} className="text-xs text-muted-foreground">
                      {apt.time} - {apt.title}
                    </p>
                  ))}
                </div>
                <Button variant="ghost" size="sm" onClick={() => document.getElementById('appointments-section')?.scrollIntoView({ behavior: 'smooth' })}>
                  Voir
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Daily Calories Progress */}
        <Card data-testid="calories-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm text-muted-foreground">Calories aujourd'hui</p>
                <p className="text-3xl font-bold font-heading">
                  {dailySummary?.consumed.calories || 0}
                  <span className="text-lg font-normal text-muted-foreground">
                    {' '}/ {dailySummary?.targets.calories || 2000} kcal
                  </span>
                </p>
              </div>
              <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                calorieProgress >= 100 ? 'bg-destructive/20' : 'bg-primary/20'
              }`}>
                <Flame className={`w-8 h-8 ${
                  calorieProgress >= 100 ? 'text-destructive' : 'text-primary'
                }`} />
              </div>
            </div>
            <Progress 
              value={calorieProgress} 
              className="h-3"
            />
            
            {/* Macros */}
            <div className="grid grid-cols-3 gap-4 mt-4">
              <div className="text-center p-2 rounded-lg bg-muted/50">
                <p className="text-lg font-bold">{dailySummary?.consumed.protein || 0}g</p>
                <p className="text-xs text-muted-foreground">
                  / {dailySummary?.targets.protein || 100}g Prot.
                </p>
              </div>
              <div className="text-center p-2 rounded-lg bg-muted/50">
                <p className="text-lg font-bold">{dailySummary?.consumed.carbs || 0}g</p>
                <p className="text-xs text-muted-foreground">
                  / {dailySummary?.targets.carbs || 250}g Gluc.
                </p>
              </div>
              <div className="text-center p-2 rounded-lg bg-muted/50">
                <p className="text-lg font-bold">{dailySummary?.consumed.fat || 0}g</p>
                <p className="text-xs text-muted-foreground">
                  / {dailySummary?.targets.fat || 65}g Lip.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bariatric Access Card - Only for bariatric patients */}
        {userProfile?.bariatric_surgery && (
          <Card 
            className="border-primary/30 bg-gradient-to-r from-primary/10 to-secondary/10 cursor-pointer hover:shadow-lg transition-all"
            onClick={() => navigate('/bariatric')}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                    <Stethoscope className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-bold">
                      Dossier {userProfile.bariatric_surgery === 'bypass' ? 'By-pass' : 'Sleeve'}
                    </h3>
                    <p className="text-xs text-muted-foreground">Suivi bariatrique personnalisé</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-4 gap-3">
          <Button
            variant="outline"
            className="flex flex-col items-center gap-1 h-auto py-4 card-interactive"
            onClick={() => navigate('/scanner')}
          >
            <Camera className="w-6 h-6 text-primary" />
            <span className="text-xs">Scanner</span>
          </Button>
          <Button
            variant="outline"
            className="flex flex-col items-center gap-1 h-auto py-4 card-interactive"
            onClick={() => navigate('/nutrition')}
          >
            <Apple className="w-6 h-6 text-secondary" />
            <span className="text-xs">Nutrition</span>
          </Button>
          <Button
            variant="outline"
            className="flex flex-col items-center gap-1 h-auto py-4 card-interactive"
            onClick={() => navigate('/workouts')}
          >
            <Dumbbell className="w-6 h-6 text-accent" />
            <span className="text-xs">Sport</span>
          </Button>
          <Button
            variant="outline"
            className="flex flex-col items-center gap-1 h-auto py-4 card-interactive"
            onClick={() => navigate('/progress')}
          >
            <TrendingUp className="w-6 h-6 text-primary" />
            <span className="text-xs">Progrès</span>
          </Button>
        </div>

        {/* Motivation Message */}
        {motivation && (
          <Card className="border-secondary/30 bg-gradient-to-r from-secondary/10 to-secondary/5">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-secondary">Citation du jour</p>
                  <p className="text-sm text-muted-foreground mt-1">{motivation.message}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Appointments Section */}
        <div id="appointments-section">
          <Card>
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Calendar className="w-5 h-5 text-primary" />
                Mes rendez-vous
              </CardTitle>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowAppointmentDialog(true)}
              >
                <CalendarPlus className="w-4 h-4 mr-1" />
                Ajouter
              </Button>
            </CardHeader>
            <CardContent>
              {sortedAppointments.length > 0 ? (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {sortedAppointments.slice(0, 5).map((apt) => {
                    const typeInfo = appointmentTypeLabels[apt.type] || appointmentTypeLabels.medical;
                    const isToday = apt.date === new Date().toISOString().split('T')[0];
                    
                    return (
                      <div 
                        key={apt.appointment_id}
                        className={`flex items-center gap-3 p-3 rounded-xl border ${
                          isToday ? 'border-primary/50 bg-primary/5' : 'border-border'
                        } ${apt.pinned ? 'ring-2 ring-primary/30' : ''}`}
                      >
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-lg ${typeInfo.color}`}>
                          {typeInfo.icon}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-sm truncate">{apt.title}</p>
                            {apt.pinned && <Pin className="w-3 h-3 text-primary" />}
                            {isToday && <Badge variant="outline" className="text-xs">Aujourd'hui</Badge>}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {new Date(apt.date).toLocaleDateString('fr-FR')} à {apt.time}
                            {apt.location && ` • ${apt.location}`}
                          </p>
                        </div>
                        <div className="flex gap-1">
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8"
                            onClick={() => togglePinAppointment(apt)}
                          >
                            <Pin className={`w-4 h-4 ${apt.pinned ? 'text-primary fill-primary' : ''}`} />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8"
                            onClick={() => {
                              setEditingAppointment(apt);
                              setNewAppointment(apt);
                              setShowAppointmentDialog(true);
                            }}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-6">
                  <Calendar className="w-12 h-12 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">Aucun rendez-vous</p>
                  <Button 
                    variant="link" 
                    size="sm" 
                    onClick={() => setShowAppointmentDialog(true)}
                  >
                    Ajouter un rendez-vous
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Daily Recipes Carousel */}
        {dailyRecipes.length > 0 && (
          <Card data-testid="daily-recipes-card">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-secondary" />
                {language === 'fr' ? 'Recettes du jour' : 'Daily Recipes'}
              </CardTitle>
              <Badge variant="outline" className="text-xs">
                {dailyRecipes.length} {language === 'fr' ? 'recettes' : 'recipes'}
              </Badge>
            </CardHeader>
            <CardContent>
              <Suspense fallback={<LazyFallback />}>
                <RecipeCarousel 
                  recipes={dailyRecipes.slice(0, 4)}
                  onRecipeClick={(recipe) => { setSelectedRecipe(recipe); setShowRecipeModal(true); }}
                  language={language}
                />
              </Suspense>
              
              <Button 
                variant="outline" 
                className="w-full mt-3"
                onClick={() => navigate('/nutrition?tab=catalog')}
              >
                {language === 'fr' ? 'Voir le catalogue de recettes' : 'View recipe catalog'}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* AI Recipe Search */}
        <Card className="border-primary/30 bg-gradient-to-br from-primary/5 to-secondary/5">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Search className="w-5 h-5 text-primary" />
              Rechercher une recette IA
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Textarea
              placeholder='Ex: "Recette avec crevettes et riz en 30 min, saine"'
              value={recipeSearchQuery}
              onChange={(e) => setRecipeSearchQuery(e.target.value)}
              rows={2}
              className="resize-none text-sm"
            />
            <Button 
              onClick={searchRecipeByAI}
              disabled={loadingSearch || !recipeSearchQuery.trim()}
              className="w-full"
              size="sm"
            >
              {loadingSearch ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Recherche...
                </>
              ) : (
                <>
                  <Search className="w-4 h-4 mr-2" />
                  Trouver ma recette
                </>
              )}
            </Button>
            
            {searchedRecipe && (
              <div className="mt-3 p-3 rounded-xl border border-primary/30 bg-background space-y-2">
                <div className="flex items-center gap-2">
                  <h4 className="font-semibold text-sm">{searchedRecipe.name}</h4>
                  {searchedRecipe.nutri_score && (
                    <span className={`w-5 h-5 rounded text-white text-xs flex items-center justify-center font-bold ${getNutriScoreColor(searchedRecipe.nutri_score)}`}>
                      {searchedRecipe.nutri_score}
                    </span>
                  )}
                </div>
                <div className="flex gap-2 text-xs text-muted-foreground">
                  <span>{searchedRecipe.calories} kcal</span>
                  <span>•</span>
                  <span>{searchedRecipe.prep_time}</span>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs"
                    onClick={() => addRecipeToFavorites(searchedRecipe)}
                  >
                    <Heart className="w-3 h-3 mr-1" />
                    Favoris
                  </Button>
                  {searchedRecipe.ingredients && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 text-xs"
                      onClick={() => addIngredientsToShoppingList(searchedRecipe.ingredients)}
                    >
                      <ListPlus className="w-3 h-3 mr-1" />
                      Courses
                    </Button>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Daily Challenges */}
        {challenges?.daily && challenges.daily.length > 0 && (
          <Card data-testid="challenges-card">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Trophy className="w-5 h-5 text-accent" />
                Défis du jour
              </CardTitle>
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold px-3">
                  🏆 {challenges.total_points || 0} pts
                </Badge>
                <span className="text-xs text-muted-foreground">
                  {challenges.daily.filter(c => c.completed).length}/{challenges.daily.length}
                </span>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {challenges.daily.map((challenge) => (
                <div 
                  key={challenge.id}
                  className={`flex items-center justify-between p-3 rounded-xl border transition-all ${
                    challenge.completed 
                      ? 'bg-gradient-to-r from-primary/10 to-secondary/10 border-primary/30' 
                      : 'border-border hover:border-primary/30'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg ${
                      challenge.completed 
                        ? 'bg-gradient-to-r from-primary to-secondary text-white' 
                        : 'bg-muted'
                    }`}>
                      {challenge.completed ? '✓' : challenge.icon}
                    </div>
                    <div className="flex-1">
                      <p className={`font-medium text-sm ${challenge.completed ? 'line-through opacity-70' : ''}`}>
                        {challenge.title}
                      </p>
                      <p className="text-xs text-muted-foreground">{challenge.description}</p>
                      {/* Progress bar */}
                      {!challenge.completed && challenge.target > 1 && (
                        <div className="mt-1">
                          <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-primary rounded-full transition-all"
                              style={{ width: `${Math.min((challenge.progress / challenge.target) * 100, 100)}%` }}
                            />
                          </div>
                          <p className="text-[10px] text-muted-foreground mt-0.5">
                            {challenge.progress.toLocaleString()}/{challenge.target.toLocaleString()}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="text-right flex items-center gap-1">
                    {challenge.completed && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          setAchievementToShare({ type: 'challenge', data: challenge });
                          setShowShareAchievementDialog(true);
                        }}
                      >
                        <Share2 className="w-3 h-3" />
                      </Button>
                    )}
                    <Badge 
                      variant={challenge.completed ? 'default' : 'outline'}
                      className={challenge.completed ? 'bg-gradient-to-r from-primary to-secondary' : ''}
                    >
                      {challenge.completed ? '✓ Fait' : `+${challenge.reward} pts`}
                    </Badge>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Social Card */}
        <Card className="shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/social')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-r from-pink-500 to-purple-500 flex items-center justify-center text-white text-xl">
                  👥
                </div>
                <div>
                  <p className="font-semibold">Communauté</p>
                  <p className="text-sm text-muted-foreground">Amis, messagerie, défis</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Appointment Dialog */}
      <Dialog open={showAppointmentDialog} onOpenChange={setShowAppointmentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingAppointment ? 'Modifier le rendez-vous' : 'Nouveau rendez-vous'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="apt-title">Titre *</Label>
              <Input
                id="apt-title"
                value={newAppointment.title}
                onChange={(e) => setNewAppointment(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Ex: RDV Médecin"
              />
            </div>
            
            <div>
              <Label htmlFor="apt-type">Type</Label>
              <Select
                value={newAppointment.type}
                onValueChange={(value) => setNewAppointment(prev => ({ ...prev, type: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="medical">🏥 Médical</SelectItem>
                  <SelectItem value="sport">🏃 Sport</SelectItem>
                  <SelectItem value="wellness">🧘 Bien-être</SelectItem>
                  <SelectItem value="nutrition">🥗 Nutrition</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="apt-date">Date *</Label>
                <Input
                  id="apt-date"
                  type="date"
                  value={newAppointment.date}
                  onChange={(e) => setNewAppointment(prev => ({ ...prev, date: e.target.value }))}
                />
              </div>
              <div>
                <Label htmlFor="apt-time">Heure *</Label>
                <Input
                  id="apt-time"
                  type="time"
                  value={newAppointment.time}
                  onChange={(e) => setNewAppointment(prev => ({ ...prev, time: e.target.value }))}
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="apt-location">Lieu</Label>
              <Input
                id="apt-location"
                value={newAppointment.location}
                onChange={(e) => setNewAppointment(prev => ({ ...prev, location: e.target.value }))}
                placeholder="Ex: Cabinet Dr. Martin"
              />
            </div>
            
            <div>
              <Label htmlFor="apt-notes">Notes</Label>
              <Textarea
                id="apt-notes"
                value={newAppointment.notes}
                onChange={(e) => setNewAppointment(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Informations supplémentaires..."
                rows={2}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <Label htmlFor="apt-pinned">Épingler ce rendez-vous</Label>
              <Switch
                id="apt-pinned"
                checked={newAppointment.pinned}
                onCheckedChange={(checked) => setNewAppointment(prev => ({ ...prev, pinned: checked }))}
              />
            </div>
          </div>
          
          <DialogFooter className="flex gap-2">
            {editingAppointment && (
              <Button 
                variant="destructive" 
                onClick={() => {
                  handleDeleteAppointment(editingAppointment.appointment_id);
                  setShowAppointmentDialog(false);
                  setEditingAppointment(null);
                }}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Supprimer
              </Button>
            )}
            <Button variant="outline" onClick={() => {
              setShowAppointmentDialog(false);
              setEditingAppointment(null);
            }}>
              Annuler
            </Button>
            <Button onClick={handleSaveAppointment}>
              {editingAppointment ? 'Modifier' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Smart Recommendation Popup */}
      <Dialog open={showRecommendation} onOpenChange={setShowRecommendation}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              Recommandation
            </DialogTitle>
          </DialogHeader>
          
          {recommendation && (
            <div className="py-4">
              <p className="text-sm leading-relaxed">{recommendation.message}</p>
            </div>
          )}
          
          <DialogFooter>
            <Button onClick={() => setShowRecommendation(false)} className="w-full">
              Merci !
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Recipe Modal */}
      <Dialog open={showRecipeModal} onOpenChange={setShowRecipeModal}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          {selectedRecipe && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {selectedRecipe.name}
                  {selectedRecipe.nutri_score && (
                    <span className={`w-6 h-6 rounded text-white text-xs flex items-center justify-center font-bold ${getNutriScoreColor(selectedRecipe.nutri_score)}`}>
                      {selectedRecipe.nutri_score}
                    </span>
                  )}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                {selectedRecipe.image && (
                  <img 
                    src={selectedRecipe.image} 
                    alt={selectedRecipe.name}
                    className="w-full h-48 rounded-lg object-cover"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                )}
                
                {/* Nutrition Info */}
                <div className="grid grid-cols-4 gap-2 text-center">
                  <div className="p-2 rounded-lg bg-muted">
                    <p className="text-lg font-bold text-primary">{selectedRecipe.calories}</p>
                    <p className="text-xs text-muted-foreground">kcal</p>
                  </div>
                  <div className="p-2 rounded-lg bg-muted">
                    <p className="text-lg font-bold">{selectedRecipe.protein || 0}g</p>
                    <p className="text-xs text-muted-foreground">Prot.</p>
                  </div>
                  <div className="p-2 rounded-lg bg-muted">
                    <p className="text-lg font-bold">{selectedRecipe.carbs || 0}g</p>
                    <p className="text-xs text-muted-foreground">Gluc.</p>
                  </div>
                  <div className="p-2 rounded-lg bg-muted">
                    <p className="text-lg font-bold">{selectedRecipe.fat || 0}g</p>
                    <p className="text-xs text-muted-foreground">Lip.</p>
                  </div>
                </div>
                
                {/* Prep Time */}
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span>⏱️ {selectedRecipe.prep_time}</span>
                  <span>👥 {selectedRecipe.servings || 4} personnes</span>
                  <span>📊 {selectedRecipe.difficulty || 'Facile'}</span>
                </div>
                
                {/* Ingredients */}
                {selectedRecipe.ingredients && (
                  <div>
                    <p className="font-medium mb-2">📝 Ingrédients</p>
                    <div className="flex flex-wrap gap-1">
                      {selectedRecipe.ingredients.map((ing, i) => (
                        <Badge key={i} variant="outline" className="text-xs">
                          {typeof ing === 'string' ? ing : `${ing.quantity || ''} ${ing.item || ing.name || ing}`}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Steps */}
                {selectedRecipe.steps && (
                  <div>
                    <p className="font-medium mb-2">👨‍🍳 Préparation</p>
                    <ol className="list-decimal list-inside space-y-2 text-sm">
                      {selectedRecipe.steps.map((step, i) => (
                        <li key={i} className="text-muted-foreground">{step}</li>
                      ))}
                    </ol>
                  </div>
                )}
                
                {/* Actions */}
                <div className="flex gap-2 pt-2">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={(e) => {
                      e.stopPropagation();
                      addRecipeToFavorites(selectedRecipe);
                    }}
                  >
                    <Heart className="w-4 h-4 mr-2" />
                    Favoris
                  </Button>
                  <Button
                    className="flex-1"
                    onClick={(e) => {
                      e.stopPropagation();
                      addIngredientsToShoppingList(selectedRecipe.ingredients);
                    }}
                  >
                    <ShoppingCart className="w-4 h-4 mr-2" />
                    Courses
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Bariatric Daily Reminder Dialog */}
      <Dialog open={showBariatricReminder} onOpenChange={setShowBariatricReminder}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-2xl">🩺</span>
              Rappel Suivi Quotidien
            </DialogTitle>
            <DialogDescription>
              Dossier {bariatricReminderData?.surgery_type || 'Bariatrique'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-4 bg-primary/10 rounded-lg text-center">
              <p className="text-lg font-semibold">
                Bonjour ! 👋
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                N'oubliez pas de remplir votre suivi quotidien pour suivre votre progression après votre opération.
              </p>
              {bariatricReminderData?.days_since_surgery && (
                <p className="text-xs text-muted-foreground mt-2">
                  📅 Jour {bariatricReminderData.days_since_surgery} post-opératoire • Phase: {bariatricReminderData.phase}
                </p>
              )}
            </div>
            
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={dismissBariatricReminder}
              >
                Plus tard
              </Button>
              <Button 
                className="flex-1"
                onClick={() => {
                  sessionStorage.setItem('bariatricReminderShown', 'true');
                  setShowBariatricReminder(false);
                  navigate('/bariatric?tab=tracking');
                }}
              >
                Remplir maintenant
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Share Achievement Dialog */}
      <Dialog open={showShareAchievementDialog} onOpenChange={setShowShareAchievementDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>📤 Partager mon accomplissement</DialogTitle>
            <DialogDescription>
              {achievementToShare?.type === 'challenge' 
                ? `Partagez votre défi terminé "${achievementToShare?.data?.title}"`
                : `Partagez votre badge "${achievementToShare?.data?.name}"`
              }
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            {/* Preview */}
            <div className="p-3 rounded-lg border bg-muted/50">
              {achievementToShare?.type === 'challenge' ? (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-r from-primary to-secondary flex items-center justify-center text-white">
                    ✓
                  </div>
                  <div>
                    <p className="font-medium text-sm">{achievementToShare?.data?.title}</p>
                    <p className="text-xs text-muted-foreground">{achievementToShare?.data?.description}</p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <span className="text-3xl">{achievementToShare?.data?.icon}</span>
                  <div>
                    <p className="font-medium text-sm">{achievementToShare?.data?.name}</p>
                    <p className="text-xs text-muted-foreground">{achievementToShare?.data?.description}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Public Wall Option */}
            <Button 
              variant={selectedShareTarget === 'public' ? 'default' : 'outline'}
              className="w-full justify-start h-auto py-3"
              onClick={() => setSelectedShareTarget('public')}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                  🌍
                </div>
                <div className="text-left">
                  <p className="font-medium">Mur public</p>
                  <p className="text-xs text-muted-foreground">Visible par toute la communauté</p>
                </div>
              </div>
            </Button>
            
            {/* User Groups */}
            {userGroups.length > 0 && (
              <>
                <p className="text-sm font-medium text-muted-foreground">Mes groupes :</p>
                {userGroups.map(group => (
                  <Button 
                    key={group.group_id}
                    variant={selectedShareTarget === group.group_id ? 'default' : 'outline'}
                    className="w-full justify-start h-auto py-3"
                    onClick={() => setSelectedShareTarget(group.group_id)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-secondary/20 flex items-center justify-center">
                        {group.name?.charAt(0) || '👥'}
                      </div>
                      <div className="text-left">
                        <p className="font-medium">{group.name}</p>
                        <p className="text-xs text-muted-foreground">{group.description?.slice(0, 40)}...</p>
                      </div>
                    </div>
                  </Button>
                ))}
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowShareAchievementDialog(false)}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button onClick={() => achievementToShare && shareAchievement(achievementToShare.type, achievementToShare.data, selectedShareTarget)}>
              <Share2 className="w-4 h-4 mr-2" />
              {language === 'fr' ? 'Partager' : 'Share'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Quick Actions Floating Button - Lazy loaded */}
      <Suspense fallback={null}>
        <QuickActionsSheet 
          onActionComplete={fetchDashboardData}
          language={language}
        />
      </Suspense>
      
      {/* AI Nutrition Chatbot - Lazy loaded */}
      <Suspense fallback={null}>
        <NutritionChatbot 
          userProfile={stats}
          language={language}
        />
      </Suspense>
    </div>
  );
}
